import axios from 'axios'
import store from '@/store'

const apiClient = axios.create({
    baseURL: '/api/v1',
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    }
})

apiClient.interceptors.request.use(config => {
    const token = store.getters.user.token;
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
}, error => {
    return Promise.reject(error);
});

const ApiService = {
    getGroups() {
        return apiClient.get('/groups');
    },
    getGroup(groupRef) {
        return apiClient.get(`/groups/${encodeURIComponent(groupRef)}`);
    },
    getAgents() {
        return apiClient.get('/agents');
    },
    createAgent(agent) {
        const payload = {
            username: (agent.username || '').trim(),
            full_name: (agent.fullName || '').trim(),
            email: (agent.email || '').trim(),
            status: (agent.status || '').trim() || 'offline',
            password: (agent.password || '').trim()
        };
        return apiClient.post('/agents', payload);
    },
    updateAgentPassword(username, password) {
        return apiClient.put(`/agents/${encodeURIComponent(username)}/password`, {
            password: (password || '').trim(),
        });
    },
    disableAgent(username) {
        return apiClient.put(`/agents/${encodeURIComponent(username)}/disable`);
    },
    enableAgent(username) {
        return apiClient.put(`/agents/${encodeURIComponent(username)}/enable`);
    },
    getDDIs() {
        return apiClient.get('/ddis');
    },
    createDDI(ddi) {
        const payload = {
            name: (ddi.name || '').trim(),
            description: (ddi.description || '').trim(),
            status: (ddi.status || '').trim() || 'active',
        };
        return apiClient.post('/ddis', payload);
    },
    disableDDI(id) {
        return apiClient.put(`/ddis/${encodeURIComponent(id)}/disable`);
    },
    enableDDI(id) {
        return apiClient.put(`/ddis/${encodeURIComponent(id)}/enable`);
    },
    createGroup(group) {
        const payload = { ...group, name: (group.name || '').trim() };
        return apiClient.post('/groups', payload);
    },
    addAgentToGroup(groupRef, agentId) {
        return apiClient.put(`/groups/${encodeURIComponent(groupRef)}/agents/${encodeURIComponent(agentId)}`);
    },
    removeAgentFromGroup(groupRef, agentId) {
        return apiClient.delete(`/groups/${encodeURIComponent(groupRef)}/agents/${encodeURIComponent(agentId)}`);
    },
    addDDIToGroup(groupRef, ddiId) {
        return apiClient.put(`/groups/${encodeURIComponent(groupRef)}/ddis/${encodeURIComponent(ddiId)}`);
    },
    removeDDIFromGroup(groupRef, ddiId) {
        return apiClient.delete(`/groups/${encodeURIComponent(groupRef)}/ddis/${encodeURIComponent(ddiId)}`);
    },
    updateGroup(groupRef, group, fallbackGroupName = '') {
        const payload = { ...group, name: (group.name || '').trim() };
        const primaryRef = encodeURIComponent(groupRef);
        const fallbackRef = encodeURIComponent(fallbackGroupName);
        const shouldRetryByName = (error) => {
            if (!fallbackGroupName || String(groupRef) === String(fallbackGroupName)) return false;
            if (!error || !error.response) return false;
            const data = error.response.data;
            if (typeof data === 'string') {
                return data.toLowerCase().includes('sql: no rows in result set');
            }
            if (data && typeof data === 'object' && data.error) {
                return String(data.error).toLowerCase().includes('sql: no rows in result set');
            }
            return false;
        };

        return apiClient
            .put(`/groups/${primaryRef}`, payload)
            .catch((error) => {
                if (!shouldRetryByName(error)) {
                    throw error;
                }
                return apiClient.put(`/groups/${fallbackRef}`, payload);
            });
    },
    deleteGroup(groupName) {
        return apiClient.delete(`/groups/${encodeURIComponent(groupName)}`);
    },
    disableGroup(groupName) {
        return apiClient.put(`/groups/${encodeURIComponent(groupName)}/disable`);
    },
    enableGroup(groupName) {
        return apiClient.put(`/groups/${encodeURIComponent(groupName)}/enable`);
    }
}

export default ApiService;
